import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Divider, Stack, Typography } from '@mui/material';
import { ArrowForwardRounded as ArrowIcon } from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { SUPPORT_EMAIL } from './legal/legalContent';

// Who built this, and why it is shaped the way it is.
//
// Third person and deliberately short. Two rules held while writing it:
// nothing here is a detail that would have to be invented to fill space,
// and every sentence is something the product can be checked against.
// The persuasive part is not the founders' biography — it is that the
// software was written inside an operation that has to run every morning
// whether or not the software is ready.

const PRINCIPLES = [
  {
    title: 'Efficiency is the point',
    body: 'A contractor’s day is decided by the minutes that go into things that are not '
      + 'driving. A screen earns its place by removing a phone call, a spreadsheet or a '
      + 'second trip to the filing cabinet.',
  },
  {
    title: 'Simple enough to hand to a new driver',
    body: 'Nobody using this is at a desk with time to learn software. One screen, one job, '
      + 'and the next step obvious without training. Depth belongs under the surface.',
  },
  {
    title: 'Right the first time',
    body: 'An operations tool that is wrong once is never trusted again. A document that was '
      + 'never filed is never shown as compliant, and nothing is claimed that the system '
      + 'cannot verify.',
  },
  {
    title: 'Worth opening',
    body: 'Software for unglamorous work is usually ugly, so people avoid it. A tool nobody '
      + 'opens changes nothing, which makes how it looks a practical concern rather than a '
      + 'vanity one.',
  },
];

const About: React.FC = () => {
  useDocumentTitle('About');

  return (
    <PublicLayout>
      <Container maxWidth="md" disableGutters>
        <Typography variant="overline" color="primary.main" display="block" sx={{ mb: 1.5 }}>
          About us
        </Typography>
        <Typography variant="h1" sx={{ mb: 2.5, maxWidth: 620 }}>
          Built inside a business that runs routes
        </Typography>
        <Typography sx={{ fontSize: 19, lineHeight: 1.65, color: 'text.secondary', maxWidth: 680, mb: 5 }}>
          TopoSDS is not software written about delivery contracting from the outside. It comes
          out of two service areas that have to be dispatched, inspected, staffed and documented
          every morning, whether or not the software is ready.
        </Typography>

        <Divider sx={{ mb: 5 }} />

        <Stack gap={2.5} sx={{ maxWidth: 720, mb: 6 }}>
          <Typography variant="h3">A contractor, and his son</Typography>
          <Typography sx={{ fontSize: 17, lineHeight: 1.75 }}>
            TopoSDS is built by a father and son. The father brought more than twenty years of
            business experience at a global level into FedEx contracting, and runs two service
            areas. The son builds the software.
          </Typography>
          <Typography sx={{ fontSize: 17, lineHeight: 1.75 }}>
            A well-run service area is usually run on one person’s memory — which truck is due
            for inspection, whose medical card lapses next month, what has to happen before 06:00
            and who has not done it. That works, and it does not copy. Add a second area and you
            are running one business twice, from memory.
          </Typography>
          <Typography sx={{ fontSize: 17, lineHeight: 1.75 }}>
            So it was written down as a system: not a filing cabinet with a login, but an engine
            that knows what the day requires, hands each person their next step, chases what
            nobody did, and keeps the proof. Everything in it exists because a real morning
            needed it.
          </Typography>
        </Stack>

        <Typography variant="h3" sx={{ mb: 3.5 }}>What it is judged on</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
            mb: 6,
          }}
        >
          {PRINCIPLES.map((principle) => (
            <Box
              key={principle.title}
              sx={{
                p: 2.75, borderRadius: 3.5,
                border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>{principle.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {principle.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: { xs: 3, md: 4 }, borderRadius: 4,
            border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h4" sx={{ mb: 1 }}>Talk to the people who built it</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            TopoSDS is made by Topo Smart Design &amp; Solutions LLC in Herndon, Virginia. The
            inbox is read by the people who write the code — and whose own operation depends on
            it being right.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
            <Button
              href={`mailto:${SUPPORT_EMAIL}?subject=TopoSDS%20enquiry`}
              variant="contained"
              size="large"
              endIcon={<ArrowIcon sx={{ fontSize: 16 }} />}
              sx={{ py: 1.25, px: 3 }}
            >
              Book a walkthrough
            </Button>
            <Button component={RouterLink} to="/" variant="outlined" size="large" sx={{ py: 1.25, px: 3 }}>
              See what it does
            </Button>
          </Stack>
        </Box>
      </Container>
    </PublicLayout>
  );
};

export default About;
